/** Contains all utility functions */
/* eslint-disable */
import queryString from 'query-string';
import isArray from 'lodash/isArray';
import snakeCase from 'lodash/snakeCase';

/**
 *
 * @type {{POST: ((p1?:*, p2?:*)), PUT: ((p1?:*, p2?:*)), DELETE: ((p1?:*)), GET: ((p1?:*))}}
 * @private
 */
const _requestAPI = {
  POST: (url, payload) =>
    fetch(url, { method: 'POST', body: JSON.stringify(payload) }),
  PUT: (url, payload) =>
    fetch(url, { method: 'PUT', body: JSON.stringify(payload) }),
  DELETE: url => fetch(url, { method: 'DELETE' }),
  GET: url => fetch(url),
};

/**
 * Creates a function that calls the callback `cb` after increasing periods until number of retries exceeds `maxTrials`
 * @param {Function} cb - The function to be retried
 * @param {Number} maxTrials - Number of max retries allowed
 */
export function exponentialBackoff(cb, maxTrials) {
  const MAX_TRIALS = maxTrials || 20;
  let trials = 0;
  return () => {
    if (trials >= MAX_TRIALS) {
      return false;
    }
    setTimeout(cb, 2 ** trials);
    trials += 1;
    return true;
  };
}

/**
 * upperSnakeCase
 * Convert a space seperated string to an upper snake cased string
 * e.g. "cat and dog" -> "CAT_AND_DOG"
 *
 **/
export function upperSnakeCase(string) {
  return snakeCase(string).toUpperCase();
}

/**
 * !!! USE 'string/camelCase' FROM LODASH INSTEAD !!!
 * https://lodash.com/docs/4.17.4#camelCase
 *
 * camelCase
 * Convert an underscore or space sepertated sting to a camel-cased string
 * e.g. "eat RHUBARB_pie" -> "eatRhubarbPie"
 *
 **/
// export function camelCase (string) {
//     let words = string.toLowerCase().split(/_| /);
//     return words.reduce((word, next) =>
//     word + next[0].toUpperCase() + next.slice(1));
// }

/**
 * !!! USE 'object/get' FROM LODASH INSTEAD !!!
 * https://lodash.com/docs/4.17.4#get
 *
 * maybe
 * Returns nested key within an object. If not found returns null
 * e.g. maybe(abc.ba.ba.c) -> "eatRhubarbPie"/undefined
 *
 **/
// export function maybe () {
//     if (arguments.length < 2) {
//         throw "you're doing it wrong";
//     }
//     let obj = _(arguments).first();
//     return _(arguments).chain().tail().reduce(function(o, prop){
//         if (!o) {
//             return o;
//         }
//         let checkForMethod = /(.*)\(\)$/.exec(prop);
//         return checkForMethod ? o[checkForMethod[1]]() : o[prop];
//     }, obj).value();
// }

/**
 * getQueryParams
 * Returns query params object
 * e.g. "?q=123&b=321" -> {q: 123, b:321}
 *
 **/
export function getQueryParams() {
  let qParams = {
    ...queryString.parse(window.location.search),
    ...queryString.parse(window.location.hash),
  };

  return qParams;
}

/**
 * getQueryParam
 * Returns specified query param value
 * e.g. getQueryParams(q) => 123
 *
 **/

export function getQueryParam(param) {
  return getQueryParams()[param];
}

/**
 * isValidEmail
 * Returns bool if given arg is a valid email
 * e.g. isValidEmail("sahil@sahil.com") => true
 * e.g. isValidEmail("sahil") => false
 * @param email
 **/

export function isValidEmail(email) {
  // eslint-disable-next-line
  let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

// eslint-disable-next-line flowtype/require-valid-file-annotation
/**
 * getEmailInitial
 * Returns the string before @ in a string
 * e.g. getEmailInitial('sahiln@flock.com') => sahiln
 * e.g. isValidEmail("sahil") => false
 * @param email
 **/

export function getEmailInitial(email) {
  return email.replace(/@.*$/, '');
}

/**
 * getEnvironment
 * Returns current project environment
 * e.g. getEnvironment() => <prod, preprod>
 *
 **/

export function getEnvironment() {
  let qParams = getQueryParams();

  if (qParams.config) {
    return qParams.config;
  }

  if (window.location.hostname.indexOf('staging') > -1) {
    return 'preprod';
  }

  return 'prod';
}

/**
 * parseToJsonPromise
 * Returns JSON result for the fetch response
 * e.g. parseToJsonPromise(data) => { ...values }
 *
 **/

function parseToJsonPromise(data) {
  return new Promise(res => {
    data.json().then(object => {
      let response = {
        statusCode: data.status,
      };

      if (isArray(object)) {
        response = {
          data: object,
          ...response,
        };
      } else {
        response = {
          ...object,
          ...response,
        };
      }

      return res(response);
    });
  });
}

/**
 * stringifyQueryParams
 * Returns stringified query params to be consumed by the URIs
 * e.g. stringifyQueryParams({ ...value }) => "?key=value&..."
 *
 **/

export function stringifyQueryParams(queryParams = {}) {
  return Object.keys(queryParams)
    .map(k => `${k}=${queryParams[k]}`)
    .join('&');
}

/**
 * constructUrl
 * Returns constructed url stitched with endpoint uri and stringified query params.
 * e.g. constructUrl("https://blahblah.com", "myname", { ...values }) => "https://blahblah.com/myname?key=value&..."
 *
 **/

export function constructUrl(endpoint, uri, queryParams) {
  let queryString = stringifyQueryParams(queryParams);
  return `${endpoint}/${uri}?${queryString || ''}`;
}

/**
 * requestApi:: Handler to request APIs given an endpoint, method and data
 * @param endpoint:: Target endpoint to which the request is made
 * @param uri:: Method on the server to be called
 * @param method:: HTTP method to be called with, <GET, POST>
 * @param data:: payload along with query params
 * @returns {Promise.<T>}
 */
export function requestApi(endpoint, uri, method, data) {
  return _requestAPI[method](
    constructUrl(endpoint, uri, data.queryParams),
    data.body
  )
    .then(parseToJsonPromise)
    .catch(error => {
      throw error;
    });
}

/**
 * timeoutPromise: returns a self destructive Promise after a certain timeout
 * @param timeout
 * @param err
 * @param promise
 * @returns {Promise}
 */
export function timeoutPromise(timeout, promise) {
  return new Promise(function(resolve, reject) {
    promise.then(resolve, reject);
    setTimeout(reject, timeout);
  });
}

export const arrayMoveMutate = (array, from, to) => {
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
};

export const arrayMove = (array, from, to) => {
  array = array.slice();
  arrayMoveMutate(array, from, to);
  return array;
};
export const AppType = (function() {
  var userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf(' electron/') > -1) {
    return 'electron';
  }
  return 'browser';
})();

export const OS = (function() {
  var userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf('mac') > -1) {
    return 'mac';
  }
  if (userAgent.indexOf('win') > -1) {
    return 'win';
  }
  return 'linux';
})();

export const OsAppType = (function() {
  return `${OS}-${AppType}`;
})();

export const getLocalMidnightTimestamp = function() {
  const now = new Date();
  now.setHours(24);
  now.setMinutes(0);
  now.setSeconds(0);

  return now;
};

const MENTIONS = 'mentions';
export function checkMention(msg) {
  if (!msg || !msg.messageElements || !msg.messageElements[MENTIONS])
    return false;
  return msg.messageElements[MENTIONS].some(mention => {
    return mention.mentionsMe;
  });
}

export function extractGuid(sessionId) {
  return sessionId.split('_')[1].split('@')[0];
}

export function maybe(obj, ...keys) {
  if (keys.length === 0) throw "you're doing it wrong";
  return keys.reduce(function(o, prop) {
    if (!o) return o;
    var checkForMethod = /(.*)\(\)$/.exec(prop);
    return checkForMethod ? o[checkForMethod[1]]() : o[prop];
  }, obj);
}



// WEBPACK FOOTER //
// ./src/utils/index.js